import { teamsApi, teamsTag } from './teams'
import type { TeamDescription } from '../types/entitys/teams'

type TeamsQuerySlice = {
  queries?: Record<
    string,
    | {
        endpointName?: string
        data?: unknown
        originalArgs?: unknown
        status?: string
      }
    | undefined
  >
}

type CachedTeamJoinPendingState = {
  hasPendingJoinRequest: boolean
  pendingJoinRequestId?: number
}

type TeamQueryCacheEntry = {
  args: { id: number | string }
  data: TeamDescription
}

function getTeamQueries(getState: () => Record<string, unknown>, teamId: number): TeamQueryCacheEntry[] {
  const slice = getState()[teamsApi.reducerPath] as TeamsQuerySlice | undefined
  const queries = slice?.queries
  if (!queries) return []

  const result: TeamQueryCacheEntry[] = []
  const seen = new Set<string>()

  for (const [cacheKey, entry] of Object.entries(queries)) {
    if (!cacheKey.startsWith('getTeam(') && entry?.endpointName !== 'getTeam') continue
    if (!entry?.data || typeof entry.data !== 'object') continue
    const data = entry.data as TeamDescription
    if (Number(data.id) !== teamId) continue
    if (!entry.originalArgs) continue

    const args = entry.originalArgs as { id: number | string }
    const argsKey = JSON.stringify(args)
    if (seen.has(argsKey)) continue
    seen.add(argsKey)
    result.push({ args, data })
  }

  return result
}

function patchGetTeamEntry(
  dispatch: (action: unknown) => unknown,
  args: { id: number | string },
  patch: (draft: TeamDescription) => void,
): { undo: () => void } | null {
  const patchResult = dispatch(
    teamsApi.util.updateQueryData('getTeam', args, (draft) => {
      if (!draft) return
      patch(draft)
    }),
  ) as { undo?: () => void } | undefined

  return patchResult?.undo ? { undo: patchResult.undo } : null
}

export function getCachedTeamJoinPendingState(
  getState: () => Record<string, unknown>,
  teamId: number,
): CachedTeamJoinPendingState {
  for (const { data } of getTeamQueries(getState, teamId)) {
    if (data.hasPendingJoinRequest === true) {
      return {
        hasPendingJoinRequest: true,
        pendingJoinRequestId: data.pendingJoinRequestId,
      }
    }
  }

  return { hasPendingJoinRequest: false }
}

export function patchCachedGetTeamByTeamId(
  dispatch: (action: unknown) => unknown,
  getState: () => Record<string, unknown>,
  teamId: number,
  patch: (draft: TeamDescription) => void,
): Array<{ undo: () => void }> {
  const undos: Array<{ undo: () => void }> = []
  const entries = getTeamQueries(getState, teamId)

  for (const { args } of entries) {
    const result = patchGetTeamEntry(dispatch, args, patch)
    if (result) undos.push(result)
  }

  if (undos.length === 0) {
    for (const idArg of [teamId, String(teamId)] as const) {
      const result = patchGetTeamEntry(dispatch, { id: idArg }, patch)
      if (result) undos.push(result)
    }
  }

  return undos
}

export function forceRefetchCachedGetTeamByTeamId(
  dispatch: (action: unknown) => unknown,
  getState: () => Record<string, unknown>,
  teamId: number,
): void {
  const entries = getTeamQueries(getState, teamId)
  const refetched = new Set<string>()

  for (const { args } of entries) {
    const key = JSON.stringify(args)
    if (refetched.has(key)) continue
    refetched.add(key)
    dispatch(
      teamsApi.endpoints.getTeam.initiate(args, { forceRefetch: true, subscribe: false }),
    )
  }

  if (refetched.size === 0) {
    for (const idArg of [teamId, String(teamId)] as const) {
      const key = JSON.stringify({ id: idArg })
      if (refetched.has(key)) continue
      refetched.add(key)
      dispatch(
        teamsApi.endpoints.getTeam.initiate({ id: idArg }, { forceRefetch: true, subscribe: false }),
      )
    }
  }
}

export function invalidateCachedTeamDetail(
  dispatch: (action: unknown) => unknown,
  teamId: number,
): void {
  dispatch(teamsApi.util.invalidateTags([{ type: teamsTag, id: `id:${teamId}` }]))
}
